import React, { useEffect, useState } from "react";
import { useDataProvider, usePermissions, useRecordContext, useUpdate } from "react-admin";
import {
  Checkbox,
  Table,
  TableContainer,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Button,
  Switch,
  FormGroup,
  FormControl,
  FormControlLabel,
  Link,
  Select,
  InputLabel,
  MenuItem,
  Typography,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { styled } from "@mui/system";
import { useNotify } from "react-admin";
import { permissionCheck, renderIfPermissions } from "../api/permissionManager";
import "./AppButton.css";
import { checkIconReqs, checkLogoReqs, checkImageRequirements } from "../resources/assetChecks";
import { sendSentryException } from "../api/SentryErrorHandler";
import { IALoading } from "./IAProgress";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import ClearIcon from "@mui/icons-material/Clear";
import ReplayIcon from "@mui/icons-material/Replay";

const StyledButton = styled(Button)({
  width: "10rem",
  height: "2.5rem",
  marginBottom: "20px",
});

const StyledTableCellButton = styled(Button)({
  "&:hover": {
    background: "#eee",
    textDecoration: "underline",
  },
  textTransform: "lowercase",
  cursor: "pointer",
});

export const TriggerBuildForm = (props) => {
  const record = useRecordContext();
  const { permissions } = usePermissions();
  const [key, setKey] = useState(record.key);
  const [ios, setIos] = useState(false);
  const [android, setAndroid] = useState(true);
  const [tagName, setTagName] = useState("");
  const [releaseChoice, setReleaseChoice] = useState("");
  const [releases, setReleases] = useState([]);
  const [useAppBranding, setUseAppBranding] = useState(false);
  const dataProvider = useDataProvider();
  const [update] = useUpdate();
  const notify = useNotify();

  const refreshTags = async () => {
    const { data } = await dataProvider.getList("app_releases", null);
    setReleases(data);
  };

  useEffect(() => {
    if (permissionCheck("app_releases", "read_only", permissions)) refreshTags("android");
  }, []);

  useEffect(() => {
    if (releaseSupportsAppBranding()) {
      setUseAppBranding(true);
    } else {
      setUseAppBranding(false);
    }
  }, [releaseChoice]);

  const releaseSupportsAppBranding = () => {
    return releaseChoice !== "" && parseFloat(releaseChoice.slice(0, 4)) >= 1.64;
  };

  const androidToggle = () => {
    setAndroid(true);
    setIos(false);
    setTagName("");
    releases.forEach((release) => {
      if (release.release_name === releaseChoice) {
        setTagName(release.android_tag);
      }
    });
  };

  const iosToggle = () => {
    setAndroid(false);
    setIos(true);
    setTagName("");
    releases.forEach((release) => {
      if (release.release_name === releaseChoice) {
        setTagName(release.ios_tag);
      }
    });
  };

  const validateAppBranding = (platform, appBranding) => {
    const { dashboard_logo, login_logo, android_app_icon, ios_app_icon, notification_icon } = appBranding;
    let appBrandingValid;
    let errorMessage;
    if (platform === "ios") {
      appBrandingValid = Boolean(login_logo && ios_app_icon);
      errorMessage = "Missing some required app branding. Make sure app branding has iOS App Icon and Login Logo set";
    } else if (platform === "android") {
      appBrandingValid = Boolean(login_logo && android_app_icon && notification_icon);
      errorMessage =
        "Missing some required app branding. Make sure app branding has Android App Icon and Login Logo set";
    }
    return [appBrandingValid, errorMessage];
  };

  const triggerBuild = async (platform) => {
    if (tagName === "") {
      notify("Error: You must select a release.", { type: "error" });
      return;
    }

    const trigger = (queryParams = {}) => {
      dataProvider
        .update("trigger_builds", {
          id: `${platform}/${tagName}`,
          data: {},
          meta: { clientKey: key },
          query: queryParams,
          body: { tag: tagName, platform },
        })
        .then(() => {
          notify(`Build triggered for ${platform} with tag ${tagName}`, { type: "success" });
        })
        .catch((error) => {
          notify("Failed to trigger build. Please try again.", { type: "error" });
          throw error;
        });
    };

    if (useAppBranding) {
      const { data } = await dataProvider.getOne("app_branding", { id: key });
      const [appBrandingValid, errorMessage] = validateAppBranding(platform, data);
      if (appBrandingValid) {
        try {
          trigger({ app_branding: true });
        } catch (error) {
          sendSentryException(error, "triggerBuild");
        }
      } else {
        alert(errorMessage, { type: "error" });
        return;
      }
    } else {
      const { data } = await dataProvider.getOne("app_assets", { id: key });
      const [appIconSuccess, dashboardLogoSuccess, loginLogoSuccess] = await Promise.all([
        checkImageRequirements(data.app_icon_url, checkIconReqs, "app-icon"),
        checkImageRequirements(data.dashboard_logo_url, checkLogoReqs, "dashboard-logo"),
        checkImageRequirements(data.login_logo_url, checkLogoReqs, "login-logo"),
      ]);
      if (appIconSuccess && dashboardLogoSuccess && loginLogoSuccess) {
        try {
          trigger();
        } catch (error) {
          sendSentryException(error, "triggerBuild");
        }
      } else {
        alert("Missing some required app assets", { type: "error" });
      }
    }
  };

  const handleSubmit = () => {
    if (ios) {
      triggerBuild("ios");
    }
    if (android) {
      triggerBuild("android");
    }
  };

  const renderIosSwitch = () => {
    if (key.length < 4 || key.substring(key.length - 4) !== "-dev") {
      return (
        <FormControlLabel
          control={<Switch checked={ios} onChange={iosToggle} name="ios" color="primary" />}
          label="iOS"
        />
      );
    }
  };

  const onTagInputChange = (event) => {
    setReleaseChoice(event.target.value);
    releases.forEach((release) => {
      if (android && release.release_name === event.target.value) {
        setTagName(release.android_tag);
      } else if (ios && release.release_name === event.target.value) {
        setTagName(release.ios_tag);
      }
    });
  };

  const renderBuildField = () => {
    let options = releases.map((opts) => opts.release_name);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel variant="outlined" id="tag-input-label">
            Select a release
          </InputLabel>
          <Select
            autoWidth
            variant="outlined"
            id="tag-input"
            labelId="tag-input-label"
            value={releaseChoice}
            label="Select a release"
            onChange={onTagInputChange}
          >
            {options &&
              options.map((option, i) => (
                <MenuItem key={i} value={option}>
                  {option}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <Tooltip>
          <FormControlLabel
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
            control={
              <Checkbox
                disabled={!releaseSupportsAppBranding()}
                data-testid={`single-trigger-app-branding`}
                checked={useAppBranding}
                onChange={(e) => setUseAppBranding(e.target.checked)}
                name="useSelfServeBranding"
                color="primary"
              />
            }
            label="Use Self-Serve App Branding"
          />
        </Tooltip>
      </div>
    );
  };

  const triggerBuildButton = renderIfPermissions(
    permissions,
    "apps",
    "write",
    <Button
      data-testid={"trigger-build"}
      color="primary"
      variant="contained"
      size="medium"
      sx={{ flex: 1, height: "2.5rem" }}
      onClick={handleSubmit}
    >
      Trigger Build
    </Button>
  );

  return (
    <div style={{ marginBottom: "2rem" }}>
      {renderIfPermissions(
        permissions,
        "app_releases",
        "read_only",
        <div style={{ width: "100%", maxWidth: "500px", minWidth: "500px" }}>
          <FormGroup>
            {renderIosSwitch()}
            <FormControlLabel
              control={<Switch checked={android} onChange={androidToggle} name="android" color="primary" />}
              label="Android"
            />
          </FormGroup>
          <div style={{ marginTop: "16px", marginBottom: "16px" }}>{renderBuildField()}</div>
        </div>
      )}
      <BuildStatusDisplay client_key={key} triggerButton={triggerBuildButton} />
    </div>
  );
};

const BuildStatusDisplay = (props) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dataProvider = useDataProvider();
  const [update] = useUpdate();
  const notify = useNotify();
  const [inputVal, setInputVal] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingArchivedBuild, setPendingArchivedBuild] = useState(null);
  const { permissions } = usePermissions();
  const [keys] = useState([
    "os",
    "build_status",
    "is_published",
    "key",
    "name",
    "build_number",
    "build_artifacts",
    "circle_url",
    "triggered_at",
    "triggered_by",
    "tag",
    "error_info",
    "archived_build",
  ]);

  const checkBuildStatus = async (client_key, currentSearch) => {
    setIsLoading(true);
    notify(currentSearch ? "Searching builds..." : "Refreshing build status...");

    try {
      const filterPayload = currentSearch ? { tag: currentSearch } : {};

      const response = await dataProvider.getList("apps", {
        id: client_key,
        meta: { statuses: true },
        filter: filterPayload,
      });

      const buildsResponse = response.data;

      if (!buildsResponse) {
        setData([]);
        setIsLoading(false);
        return;
      }

      const finalBuilds = await Promise.all(
        buildsResponse.map(async (build) => {
          if (!build.build_status || build.build_status === "null") {
            const statusResponse = await getStatus(build.pipeline_uuid);
            build.build_status = statusResponse.errors || "pending...";
          }
          return build;
        })
      );

      setData(finalBuilds);
    } catch (err) {
      notify("Error fetching data", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkBuildStatus(props.client_key, "");
  }, [props.client_key]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setSearchQuery(inputVal);
    checkBuildStatus(props.client_key, inputVal);
  };

  const handleClearSearch = () => {
    setInputVal("");
    setSearchQuery("");
    checkBuildStatus(props.client_key, "");
  };

  const getStatus = async (uuid) => {
    return await dataProvider.getOne("apps", { id: uuid, meta: { status: true } });
  };

  const changePublishStatus = async (event, pipeline_uuid) => {
    await update("apps", { id: pipeline_uuid, meta: { checked: event.target.checked } });
  };

  // Guard on permissions being loaded: permissionCheck dereferences it directly when granular
  // permissions are on. Dropping the key entirely avoids showing read-only users an empty column.
  const canSeeArchivedBuilds = Boolean(permissions) && permissionCheck("apps", "write", permissions);
  const visibleKeys = canSeeArchivedBuilds ? keys : keys.filter((k) => k !== "archived_build");

  const rowPlatform = (row) => {
    if (!row?.["circle_url"]) return null;
    const url = row["circle_url"].toLowerCase();
    if (url.includes("android")) return "android";
    if (url.includes("iphone") || url.includes("ios")) return "ios";
    return null;
  };

  // An archived build has a build_number but no artifact in S3. The archived build job only
  // downloads from S3, it never uploads, so it cannot be used as a source.
  const canTriggerArchivedBuild = (row) =>
    Boolean(row["build_number"]) && rowPlatform(row) !== null && !row["archive_build_id"];

  const archivedBuildDisabledReason = (row) => {
    if (row["archive_build_id"]) return "Archived builds cannot be re-triggered";
    return "No build number or platform for this build";
  };

  const triggerArchivedBuild = (row) => {
    const platform = rowPlatform(row);
    setPendingArchivedBuild(null);

    dataProvider
      .create("archived_builds", {
        data: { archive_build_id: row["build_number"] },
        meta: { clientKey: props.client_key, platform },
      })
      .then(() => {
        notify(`Archived build triggered for ${platform} build ${row["build_number"]}`, { type: "success" });
      })
      .catch((error) => {
        notify("Failed to trigger archived build. Please try again.", { type: "error" });
        sendSentryException(error, "triggerArchivedBuild");
      });
  };

  const renderTableCell = (row, key) => {
    switch (key) {
      case "os":
        if (!row["circle_url"]) return "N/A";
        const url = row["circle_url"].toLowerCase();
        if (url.includes("android")) {
          return <AndroidIcon color="action" />;
        } else if (url.includes("iphone") || url.includes("ios")) {
          return <AppleIcon color="action" />;
        }
        return "N/A";
      case "build_status":
        return <>{row[key]}</>;
      case "circle_url":
        return <StyledTableCellButton onClick={() => window.open(row[key])}>{row[key]}</StyledTableCellButton>;
      case "tag":
        // An archived build has no meaningful ref of its own -- it was triggered against master
        // purely to get the Fastfile -- so show which build it came from instead.
        if (row.archive_build_id) return <>Archived build of {row.archive_build_id}</>;
        return row.branch === null || undefined ? <>{row[key]}</> : <>{row.branch}</>;
      case "build_artifacts":
        const build_files = row[key];
        if (Object.keys(build_files).length === 0) {
          return <div sx={{ whiteSpace: "nowrap" }}>No Build Files</div>;
        } else {
          return (
            <div sx={{ whiteSpace: "nowrap" }}>
              {Object.keys(build_files).map((k, i) => (
                <p key={k}>
                  <span>
                    <Link href={build_files[k]} download>
                      {k.slice(0, 3).toUpperCase()} Build File
                    </Link>
                  </span>
                </p>
              ))}
            </div>
          );
        }
      case "is_published":
        const is_published = row[key];
        return (
          <Checkbox
            name="published status"
            defaultChecked={is_published}
            onChange={(e) => changePublishStatus(e, row["pipeline_uuid"])}
          />
        );
      case "archived_build":
        return (
          <Tooltip title={canTriggerArchivedBuild(row) ? "Trigger archived build" : archivedBuildDisabledReason(row)}>
            {/* span wrapper: a disabled element fires no mouse events, so Tooltip needs it */}
            <span>
              <IconButton
                aria-label="Trigger archived build"
                data-testid={`archived-build-${row["build_number"]}`}
                disabled={!canTriggerArchivedBuild(row)}
                onClick={() => setPendingArchivedBuild(row)}
                color="primary"
              >
                <ReplayIcon />
              </IconButton>
            </span>
          </Tooltip>
        );
      case "error_info":
        const error_info = row[key];
        if (error_info === null || error_info.length === 0) {
          return "None";
        } else {
          return (
            <>
              {(() => {
                const blob = new Blob([error_info[0]], {
                  type: "text/plain",
                });
                const url = URL.createObjectURL(blob);
                return (
                  <Link href={url} target="_blank" display="block" download="build_error">
                    Build Error
                  </Link>
                );
              })()}
            </>
          );
        }
      default:
        return row[key];
    }
  };

  return (
    <div>
      <div
        style={{
          width: "100%",
          maxWidth: "500px",
          minWidth: "400px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <div style={{ display: "flex", gap: "10px" }}>
          {props.triggerButton}

          <Button
            color="primary"
            variant="contained"
            sx={{ flex: 1, height: "2.5rem" }}
            onClick={() => checkBuildStatus(props.client_key, searchQuery)}
          >
            Refresh
          </Button>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <TextField
            label="Search builds by tag/branch..."
            variant="outlined"
            size="small"
            data-testid={"app-build-search-input"}
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            sx={{ flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearchSubmit();
              }
            }}
            InputProps={{
              endAdornment: inputVal ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear search"
                    onClick={handleClearSearch}
                    edge="end"
                    size="small"
                    data-testid={"app-build-search-clear"}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Button
            variant="contained"
            color="primary"
            size="medium"
            data-testid={"app-build-search-btn"}
            sx={{ height: "2.5rem" }}
            onClick={handleSearchSubmit}
          >
            Search
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Typography style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "20px" }}>
          Fetching build data...
          <IALoading />
        </Typography>
      ) : data.length === 0 ? (
        <Typography style={{ marginTop: "20px" }}>
          {searchQuery ? "No search results found :(" : "No builds yet :("}
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead style={{ textTransform: "uppercase" }}>
              <TableRow>
                {visibleKeys.map((key) => (
                  <TableCell key={key} data-testid={`build-status-header-${key}`}>
                    {key.replaceAll("_", " ")}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(data) &&
                data
                  .filter(Boolean)
                  .map((row, i) => (
                    <TableRow key={row?.pipeline_uuid ?? `row-${i}`}>
                      {Array.isArray(visibleKeys) &&
                        visibleKeys
                          .filter(Boolean)
                          .map((k) => (
                            <TableCell key={`${row?.pipeline_uuid ?? `row-${i}`}-${k}`}>
                              {renderTableCell(row, k)}
                            </TableCell>
                          ))}
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(pendingArchivedBuild)} onClose={() => setPendingArchivedBuild(null)}>
        <DialogTitle>
          Trigger archived {rowPlatform(pendingArchivedBuild) === "ios" ? "iOS" : "Android"} build {pendingArchivedBuild?.build_number} for{" "}
          {props.client_key}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Re-signs the archived binary from build {pendingArchivedBuild?.build_number} and uploads it to Firebase.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            This <i>will not</i> create a new build from scratch and depends on the build's existence in s3.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingArchivedBuild(null)}>Cancel</Button>
          <Button
            data-testid="confirm-archived-build"
            color="primary"
            variant="contained"
            onClick={() => triggerArchivedBuild(pendingArchivedBuild)}
          >
            Trigger archived build
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
